// ============================================================================
// EXPENSE MASTER SCREEN — Phase G
// ============================================================================
// List of expense vouchers + Add/Edit modal entry-point.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { useExpenses } from 'hooks/api/useExpenses';
import { useExpenseTypes } from 'hooks/api/useExpenseTypes';
import { expenseService } from 'api/services/expenseService';
import type {
  Expense,
  ExpenseListItem,
  ExpenseSearchRequest,
} from 'api/types/expense.types';
import ExpenseFormModal from 'components/modals/modules/expense-module/ExpenseFormModal';

interface ExpenseMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const fmtMoney = (n?: number | null) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN'); } catch { return s; }
};

const ExpenseMasterScreen: React.FC<ExpenseMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<ExpenseSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useExpenses(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [expenseTypeFilter, setExpenseTypeFilter] = useState('');
  const [fromDateFilter, setFromDateFilter] = useState('');
  const [toDateFilter, setToDateFilter] = useState('');

  // Form modal state.
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editRecord, setEditRecord] = useState<Expense | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Minimize support — multi-instance Add.
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(e => e.transactionNo === recordCode);
        if (rec) {
          // Re-fetch the full voucher for edit restore.
          expenseService.getById(rec.id).then(full => {
            if (full) setEditRecord(full);
          });
        }
      }
      setFormMode(restoredMode);
      setFormOpen(true);
    },
    [data],
  );

  // Delete state.
  const [deleteTarget, setDeleteTarget] = useState<ExpenseListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Lookups.
  const { data: expenseTypes } = useExpenseTypes({ page: 1, pageSize: 200, statusCode: 'ACTIVE' });
  const expenseTypeOptions = useMemo(
    () => [
      { value: '', label: 'All heads' },
      ...(expenseTypes ?? []).map(t => ({ value: t.id, label: t.expenseName })),
    ],
    [expenseTypes],
  );

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery.trim() || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      expenseTypeId: expenseTypeFilter || undefined,
      fromDate: fromDateFilter || undefined,
      toDate: toDateFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [expenseTypeFilter, fromDateFilter, toDateFilter]);

  const handleClearFilters = useCallback(() => {
    setExpenseTypeFilter('');
    setFromDateFilter('');
    setToDateFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (queryParams.expenseTypeId) c++;
    if (queryParams.fromDate) c++;
    if (queryParams.toDate) c++;
    return c;
  }, [queryParams]);

  const handleAdd = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setEditRecord(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback(async (item: ExpenseListItem) => {
    // If this voucher is already minimized, restore it instead of opening fresh.
    const modalId = tabId ? `${tabId}-form-modal-edit-${item.transactionNo}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setLoadingEdit(true);
    try {
      const full = await expenseService.getById(item.id);
      if (!full) {
        notificationService.toast.error('Expense not found.');
        return;
      }
      setEditRecord(full);
      setFormMode('edit');
      setFormOpen(true);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to load expense.', 6000);
    } finally {
      setLoadingEdit(false);
    }
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await expenseService.delete(deleteTarget.id);
      setIsDeleting(false);
      setDeleteTarget(null);
      notificationService.toast.success(`Expense "${deleteTarget.transactionNo}" deleted.`);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.', 6000);
    }
  }, [deleteTarget, refetch]);

  // ----- Columns -----

  const columns: SyutGridColumn<ExpenseListItem>[] = useMemo(() => [
    { id: 'transactionNo', header: 'Txn No', accessorKey: 'transactionNo', size: 150, enableSorting: true,
      cell: ({ row }) => <strong>{row.original.transactionNo}</strong>,
    },
    { id: 'transactionDate', header: 'Date', accessorKey: 'transactionDate', size: 110,
      cell: ({ row }) => fmtDate(row.original.transactionDate),
    },
    { id: 'paidTo', header: 'Paid To', accessorKey: 'paidTo', size: 220, flex: true,
      cell: ({ row }) => row.original.paidTo || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    { id: 'refNo', header: 'Ref No', accessorKey: 'refNo', size: 130,
      cell: ({ row }) => row.original.refNo || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    { id: 'headsSummary', header: 'Heads', accessorKey: 'headsSummary', size: 260,
      cell: ({ row }) => (
        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          {row.original.headsSummary || '—'}
        </span>
      ),
    },
    { id: 'lineCount', header: 'Lines', accessorKey: 'lineCount', size: 70 },
    { id: 'allocationCount', header: 'Allocations', accessorKey: 'allocationCount', size: 90 },
    { id: 'totalAmount', header: 'Total', accessorKey: 'totalAmount', size: 130,
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {fmtMoney(row.original.totalAmount)}
        </span>
      ),
    },
    { id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 100,
      cell: ({ row }) => {
        const code = row.original.deletedAt ? 'DELETED' : (row.original.statusCode || 'ACTIVE');
        const color = code === 'ACTIVE' ? '#16a34a' : code === 'DELETED' ? '#dc2626' : '#6b7280';
        return (
          <span style={{
            fontSize: '0.72rem', fontWeight: 600, color,
            border: `1px solid ${color}`, borderRadius: 999, padding: '1px 8px',
          }}>{code}</span>
        );
      },
    },
  ], []);

  const getRowActions = useCallback((r: ExpenseListItem): ActionMenuItem[] => [
    { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit size={16} />,  onClick: () => handleEdit(r) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => setDeleteTarget(r), variant: 'danger' },
  ], [handleEdit]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add',     icon: <IconSystem.add size={18} />,     label: 'Add',     tooltip: 'New expense',  onClick: handleAdd,        variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh list', onClick: () => refetch(),  variant: 'icon' },
  ], [handleAdd, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Expenses"
        description="Litigation expense vouchers — bill header with multiple heads, each apportioned across cases (direct or by group)."
        icon={<IconSystem.dollarSign size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search txn / paid-to / ref…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        actions={actionButtons}
        customToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              isLoading={loading || loadingEdit}
              emptyMessage="No expenses recorded yet"
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button
                onClick={() => { handleClearFilters(); setFilterOpen(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}
                title="Close filters"
              >
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Expense Head"
                  options={expenseTypeOptions}
                  value={expenseTypeFilter}
                  onChange={v => setExpenseTypeFilter((v as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={fromDateFilter}
                  onChange={e => setFromDateFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={toDateFilter}
                  onChange={e => setToDateFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete expense?"
        message={deleteTarget ? `Delete expense "${deleteTarget.transactionNo}" (${fmtMoney(deleteTarget.totalAmount)})? Its lines and allocations are removed too.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => { if (!isDeleting) setDeleteTarget(null); }}
      />

      <ExpenseFormModal
        isOpen={formOpen}
        mode={formMode}
        record={editRecord}
        onSaved={() => refetch()}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        tabId={tabId}
        moduleName={moduleName}
        createInstanceId={createInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default ExpenseMasterScreen;
